/**
 * React Query hooks for Actions module
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  ActionItem,
  ActionItemsGrouped,
  CreateActionItemData,
  UpdateActionItemData,
  ActionFilters,
  Comment,
  CreateCommentData,
  Label,
  CreateLabelData,
} from '@/types/actions'

/**
 * Build query string from filters
 */
function buildQueryString(filters?: ActionFilters): string {
  if (!filters) return ''
  
  const params = new URLSearchParams()
  
  if (filters.status) params.append('status', filters.status)
  if (filters.priority) params.append('priority', filters.priority)
  if (filters.assignee) params.append('assignee', filters.assignee)
  if (filters.unassigned) params.append('unassigned', 'true')
  if (filters.my_tasks) params.append('my_tasks', 'true')
  if (filters.labels) params.append('labels', filters.labels)
  if (filters.search) params.append('search', filters.search)
  if (filters.archived) params.append('archived', 'true')
  
  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * Hook to fetch all action items grouped by status
 */
export function useActionItems(filters?: ActionFilters) {
  const queryString = buildQueryString(filters)
  
  return useQuery<ActionItemsGrouped>({
    queryKey: ['actions', filters],
    queryFn: async () => {
      const response = await api.get(`/actions/${queryString}`)
      return response.data
    },
    staleTime: 0,
  })
}

/**
 * Hook to fetch single action item with details
 */
export function useActionItem(actionId: string | undefined) {
  return useQuery<ActionItem>({
    queryKey: ['action', actionId],
    queryFn: async () => {
      const response = await api.get(`/actions/${actionId}/`)
      return response.data
    },
    enabled: !!actionId,
  })
}

/**
 * Hook to create action item
 */
export function useCreateActionItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateActionItemData) => {
      const response = await api.post('/actions/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
    },
  })
}

/**
 * Hook to update action item
 */
export function useUpdateActionItem(actionId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: UpdateActionItemData) => {
      const response = await api.patch(`/actions/${actionId}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      queryClient.invalidateQueries({ queryKey: ['action', actionId] })
    },
  })
}

/**
 * Hook to move action item to different status (drag and drop)
 */
export function useMoveActionItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ actionId, status }: { actionId: string; status: string }) => {
      const response = await api.post(`/actions/${actionId}/move/`, { status })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
    },
  })
}

/**
 * Hook to archive action item
 */
export function useArchiveActionItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (actionId: string) => {
      const response = await api.post(`/actions/${actionId}/archive/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
    },
  })
}

/**
 * Hook to unarchive action item
 */
export function useUnarchiveActionItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (actionId: string) => {
      const response = await api.post(`/actions/${actionId}/unarchive/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
    },
  })
}

/**
 * Hook to fetch comments for action item
 */
export function useActionComments(actionId: string | undefined) {
  return useQuery<Comment[]>({
    queryKey: ['action-comments', actionId],
    queryFn: async () => {
      const response = await api.get(`/actions/${actionId}/comments/`)
      return response.data
    },
    enabled: !!actionId,
  })
}

/**
 * Hook to add comment to action item
 */
export function useAddComment(actionId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateCommentData) => {
      const response = await api.post(`/actions/${actionId}/comments/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-comments', actionId] })
      queryClient.invalidateQueries({ queryKey: ['action', actionId] })
      queryClient.invalidateQueries({ queryKey: ['actions'] })
    },
  })
}

/**
 * Hook to fetch all labels
 */
export function useLabels() {
  return useQuery<Label[]>({
    queryKey: ['labels'],
    queryFn: async () => {
      const response = await api.get('/actions/labels/')
      return response.data
    },
  })
}

/**
 * Hook to create label
 */
export function useCreateLabel() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateLabelData) => {
      const response = await api.post('/actions/labels/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labels'] })
    },
  })
}
